import { useMemo } from 'react'

import { getListedChainIds } from '~/constants/chains'
import { useActiveOrgSmartAccountsChainIds } from '~/hooks/org'
import { useAggregatedBalances } from '~/hooks/useAggregatedBalances'
import { IAddress } from '~/types'

export type SelectedToken = { address: string; chainId: number } | null

export function useSelectedAggregatedBalance({
  token,
  filterAccountAddress,
}: {
  token: SelectedToken
  filterAccountAddress?: IAddress
}) {
  const filterAccountAddresses = useMemo(
    () => (filterAccountAddress ? [filterAccountAddress] : undefined),
    [filterAccountAddress],
  )

  const { balances } = useAggregatedBalances({
    filterAccountAddresses,
  })

  // Compute active chain IDs (org chains or default supported chains)
  const orgChainIds = useActiveOrgSmartAccountsChainIds()
  const activeChainIds = useMemo(
    () => (orgChainIds.length > 0 ? orgChainIds : getListedChainIds()),
    [orgChainIds],
  )

  const balance = useMemo(() => {
    if (!token) return undefined

    return balances.find(
      (b) =>
        b.chainId === token.chainId &&
        b.address.toLowerCase() === token.address.toLowerCase(),
    )
  }, [balances, token])

  // Token is on an external chain if it's not in the org's active chains
  const isExternalChain = !!token && !activeChainIds.includes(token.chainId)

  return { balance, isExternalChain }
}
